import { AutocompleteChangeDetails, AutocompleteChangeReason } from "@mui/material";
import { SyntheticEvent, useContext, useState } from "react";
import { useCandidateToGroupsMutation } from "../../../../../../App/Queries/CandidateQueries";
import { useGroupsQuery } from "../../../../../../App/Queries/GroupQueries";
import { useNotification } from "../../../../../../App/State/Hooks/notificationHooks";
import { useDebouncedHandler } from "../../../../../../App/State/Hooks/useDebouncedHandler";
import { IGroupFilter } from "../../../../../../App/Typings/Groups";
import { CandidateContext } from "../../../../../CandidateProfile";

export function useAddToGroupsState(setShowAddToGroups: (value: boolean) => void) {
  const { candidate } = useContext(CandidateContext);
  const candidateGroups = new Set(candidate.groups.map((group) => group.id));
  const { data: groupDropdownList = [] } = useGroupsQuery<IGroupFilter[]>(false, (data) =>
    data.items
      .filter(({ id, isDynamic }) => !isDynamic && !candidateGroups.has(id))
      .map(({ id, name }) => ({ id, name })),
  );
  const { mutateAsync: addCandidateToGroups, isLoading: isLoadingAddToGroups } =
    useCandidateToGroupsMutation();
  const { displayError, displaySuccess, displayWarning } = useNotification();

  const [selectedGroups, setSelectedGroups] = useState<IGroupFilter[]>([]);

  const handleCancelAddToGroup = () => {
    setShowAddToGroups(false);
    setSelectedGroups([]);
  };

  /**
   * @description stores currently selected groups
   * @param event
   * @param newValue
   */
  const handleGroupsSelect = (
    _: SyntheticEvent,
    newValue: IGroupFilter[],
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<IGroupFilter> | undefined,
  ) => {
    if (reason === "clear") {
      setSelectedGroups([]);
      return;
    }
    if (reason === "removeOption" && details) {
      setSelectedGroups(selectedGroups.filter((group) => group.id !== details.option.id));
      return;
    }
    setSelectedGroups(newValue);
  };

  const handleGroupSave = useDebouncedHandler(async () => {
    if (selectedGroups.length === 0) {
      displayWarning("Please select at least one group from the dropdown");
      return;
    }
    if (isLoadingAddToGroups) {
      return;
    }
    await addCandidateToGroups(
      {
        candidateId: candidate.id,
        groupIds: selectedGroups.map((group) => group.id),
      },
      {
        onError: () => displayError("There was an error saving candidate to groups. Please try again"),
        onSuccess: () => {
          setShowAddToGroups(false);
          displaySuccess("Candidate groups saved successfully!");
        },
      },
    );
  }, 300);

  return {
    getters: {
      groupDropdownList,
      selectedGroups,
      isLoadingAddToGroups,
    },
    handlers: {
      handleCancelAddToGroup,
      handleGroupSave,
      handleGroupsSelect,
    },
  };
}
